import type { SortingState } from "@tanstack/react-table";
import { z } from "zod/v4-mini";

import { ApiDataSourceSpec, apiDataSource } from "@/io/datasource/ApiDatasource.ts";
import { LocalStorageDataSourceSpec, localStorageDataSource } from "@/io/datasource/LocalStorageDatasource.ts";
import {
  type SessionStorageDataSourceSpec,
  sessionStorageDataSource,
} from "@/io/datasource/SessionStorageDatasource.ts";
import { FeatureFlags, featureFlagsSchema } from "@/models/FeatureFlagModel.ts";
import {
  BGMPreferences,
  bgmPreferencesSchema,
  PatientListColumnVisibility,
  patientListColumnVisibilitySchema,
  type PatientListColumnId,
} from "@/models/UISettingsModel.ts";
import { ExtendedUser, extendedUserSchema } from "@/models/UserModel.ts";
import type { PatientListMenuItems } from "@/uiKit/organisms/PatientsList/PatientListMenu.tsx";
import { cz } from "@/utils/zodUtils.ts";

export class UserRepository {
  constructor(
    private readonly datasource: ApiDataSourceSpec = apiDataSource,
    private readonly localDatasource: LocalStorageDataSourceSpec = localStorageDataSource,
    private readonly sessionDatasource: SessionStorageDataSourceSpec = sessionStorageDataSource,
  ) {}

  getUser = (): Promise<ExtendedUser> => {
    return this.datasource.get("practitioner/me/", extendedUserSchema);
  };

  getFeatureFlags = (): Promise<FeatureFlags> => {
    return this.datasource.get("/feature-flags/", featureFlagsSchema);
  };

  getBGMPreferences = async (userId: string): Promise<BGMPreferences> => {
    const result = await this.localDatasource.retrieve(userId, "BGMPreferences", bgmPreferencesSchema);
    return result ?? {};
  };

  updateBGMPreferences = async (userId: string, data: BGMPreferences) => {
    await this.localDatasource.store(userId, "BGMPreferences", data);
  };

  getPatientListColumnVisibility = async (userId: string): Promise<PatientListColumnVisibility> => {
    const result = await this.localDatasource.retrieve(
      userId,
      "PatientListColumnVisibility",
      patientListColumnVisibilitySchema,
    );
    return result ?? {};
  };

  updatePatientListColumnVisibility = async (
    userId: string,
    tab: PatientListMenuItems,
    key: PatientListColumnId,
    state: boolean,
  ) => {
    const currentData = await this.getPatientListColumnVisibility(userId);
    const result = {
      ...currentData,
      [tab]: {
        ...(currentData?.[tab] ?? {}),
        [key]: state,
      },
    };
    await this.localDatasource.store(userId, "PatientListColumnVisibility", result);
  };

  resetTabToDefaults = async (userId: string, tab: PatientListMenuItems) => {
    const currentData = await this.getPatientListColumnVisibility(userId);
    const result = {
      ...currentData,
      [tab]: {},
    };
    await this.localDatasource.store(userId, "PatientListColumnVisibility", result);
  };

  getPatientListSorting = async (userId: string): Promise<SortingState> => {
    const result = await this.sessionDatasource.retrieve(
      userId,
      "patientListSort",
      cz.forcedArray(
        z.object({
          id: z.string(),
          desc: z.boolean(),
        }),
      ),
    );
    return result ?? [];
  };

  updatePatientListSorting = (userId: string, sorting: SortingState) =>
    this.sessionDatasource.store(userId, "patientListSort", sorting);
}
